import React, { useEffect, useState } from 'react'
import { BrowserRouter as Router, Routes, Route, useLocation, useParams, Outlet } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { I18nextProvider, useTranslation } from 'react-i18next'
import i18n from './i18n/i18n'
import Header from './components/layout/Header'
import Footer from './components/layout/Footer'
import HomePage from './pages/HomePage'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import CompanionPage from './pages/CompanionPage'
import DailyCompanionPage from './pages/DailyCompanionPage'
import SeniorCarePage from './pages/SeniorCarePage'
import MentalWellnessPage from './pages/MentalWellnessPage'
import AIChatPage from './pages/AIChatPage'
import PetPage from './pages/PetPage'
import DigitalHumanPage from './pages/DigitalHumanPage'
import ShopPage from './pages/ShopPage'
import VirtualLovePage from './pages/VirtualLovePage'
import DigitalHumanExperiencePage from './pages/DigitalHumanExperiencePage'
import MVPTestPage from './pages/MVPTestPage'
import MVPChinaPage from './pages/MVPChinaPage'
import WarmStoriesPage from './pages/WarmStoriesPage'
import DigitalPersonaHubPage from './pages/DigitalPersonaHubPage'
import LegacyAssetWorkspacePage from './pages/LegacyAssetWorkspacePage'
import HistoricalPersonaWorkspacePage from './pages/HistoricalPersonaWorkspacePage'
import AboutPage from './pages/AboutPage'
import TeamPage from './pages/TeamPage'
import ContactPage from './pages/ContactPage'
import JoinPage from './pages/JoinPage'
import VRPage from './pages/VRPage'
import AllInOnePage from './pages/AllInOnePage'
import PrivacyPage from './pages/PrivacyPage'
import TermsPage from './pages/TermsPage'
import ReunionSpacePage from './pages/ReunionSpacePage'
import FamilyTreePage from './pages/FamilyTreePage'
import FamilyGalaxyPage from './pages/FamilyGalaxyPage'
import HistoryHallPage from './pages/HistoryHallPage'
import HistoryDialoguePage from './pages/HistoryDialoguePage'
import HistoryCreatorPage from './pages/HistoryCreatorPage'
import MemorySystemPage from './pages/MemorySystemPage'
import DigitalWorldPage from './pages/DigitalWorldPage'
import NotFoundPage from './pages/NotFoundPage'
import SitemapPage from './pages/SitemapPage'
import { Helmet } from 'react-helmet-async'
import DashboardPage from './pages/DashboardPage'
import ProtectedRoute from './components/auth/ProtectedRoute'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import UpdatePasswordPage from './pages/UpdatePasswordPage'
import FounderColumnPage from './pages/FounderColumnPage'
import PetArchivePage from './pages/PetArchivePage' // Deprecated, keeping for ref if needed, but likely replaced
import PetLandingPage from './pages/PetLandingPage'
import PetDashboardPage from './pages/PetDashboardPage'
import PetDetailPage from './pages/PetDetailPage'
import BabyResumePage from './pages/BabyResumePage'
import SpeakBarPage from './pages/SpeakBarPage'
import AnalyticsTracker from './components/analytics/AnalyticsTracker'
import OfflinePage from './pages/OfflinePage'

// 1. 自动回顶组件
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// 2. 语言同步逻辑组件
const LanguageSync = ({ lang }) => {
  const { i18n: i18nInstance } = useTranslation();

  useEffect(() => {
    const supportedLangs = ['en', 'zh-TW', 'ja', 'ko', 'es'];
    if (lang && supportedLangs.includes(lang)) {
      if (i18nInstance.language !== lang) {
        console.log(`Syncing i18n language to URL param: ${lang}`);
        i18nInstance.changeLanguage(lang);
      }
    }
  }, [lang, i18nInstance]);

  return (
    <Helmet>
      <html lang={i18nInstance.language} />
    </Helmet>
  );
};

// 3. 通用布局外框
const AppLayout = () => {
  const { lang } = useParams();
  const location = useLocation();
  const hideHeaderFooterRoutes = ['/companion', '/speak-bar'];
  const shouldHideHeaderFooter = hideHeaderFooterRoutes.some(route => location.pathname.includes(route));

  return (
    <div className="min-h-screen flex flex-col">
      <LanguageSync lang={lang} />
      <AnalyticsTracker />
      {!shouldHideHeaderFooter && <Header />}
      <main className="flex-grow">
        <Outlet />
      </main>
      {!shouldHideHeaderFooter && <Footer />}
    </div>
  );
};

// 4. 业务路由定义 (相对路径)
const BusinessRoutes = () => (
  <>
    <Route index element={<HomePage />} />
    <Route path="dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
    <Route path="login" element={<LoginPage />} />
    <Route path="forgot-password" element={<ForgotPasswordPage />} />
    <Route path="update-password" element={<UpdatePasswordPage />} />
    <Route path="register" element={<RegisterPage />} />
    <Route path="companion" element={<CompanionPage />} />
    <Route path="companion/daily" element={<DailyCompanionPage />} />
    <Route path="companion/senior" element={<SeniorCarePage />} />
    <Route path="companion/mental" element={<MentalWellnessPage />} />
    <Route path="companion/chat" element={<ProtectedRoute><AIChatPage /></ProtectedRoute>} />
    <Route path="virtual-pet" element={<PetPage />} />
    <Route path="pet" element={<ProtectedRoute><PetDashboardPage /></ProtectedRoute>} />
    <Route path="pet/p/:slug" element={<PetDetailPage />} />
    <Route path="baby-resume" element={<BabyResumePage />} />
    <Route path="speak-bar" element={<SpeakBarPage />} />
    <Route path="virtual-love" element={<VirtualLovePage />} />
    <Route path="virtual-love/manage" element={<DigitalPersonaHubPage type="lover" />} />
    <Route path="digital-immortality" element={<DigitalPersonaHubPage type="immortality" />} />
    <Route path="digital-immortality/create" element={<DigitalPersonaHubPage type="immortality" />} />
    <Route path="digital-immortality/assets/:profileId" element={<ProtectedRoute><LegacyAssetWorkspacePage /></ProtectedRoute>} />
    <Route path="shop" element={<ShopPage />} />
    <Route path="digital-rebirth" element={<DigitalPersonaHubPage type="rebirth" />} />
    <Route path="digital-rebirth/historical" element={<DigitalPersonaHubPage type="rebirth" defaultMode="historical_rebirth" />} />
    <Route path="digital-rebirth/historical/:profileId" element={<ProtectedRoute><HistoricalPersonaWorkspacePage /></ProtectedRoute>} />
    <Route path="digital-rebirth/reunion-space" element={<ReunionSpacePage />} />
    <Route path="digital-rebirth/family-tree" element={<FamilyTreePage />} />
    <Route path="digital-rebirth/family-galaxy" element={<FamilyGalaxyPage />} />
    <Route path="digital-rebirth/history-hall" element={<HistoryHallPage />} />
    <Route path="digital-rebirth/history-dialogue/:id" element={<HistoryDialoguePage />} />
    <Route path="digital-rebirth/history-creator" element={<HistoryCreatorPage />} />
    <Route path="digital-rebirth/create" element={<DigitalPersonaHubPage type="rebirth" />} />
    <Route path="memory-system" element={<ProtectedRoute><MemorySystemPage /></ProtectedRoute>} />
    <Route path="memories/:profileId" element={<ProtectedRoute><MemorySystemPage /></ProtectedRoute>} />
    <Route path="chat/:profileId" element={<ProtectedRoute><MemorySystemPage /></ProtectedRoute>} />
    <Route path="start-experience" element={<DigitalHumanPage />} />
    <Route path="about" element={<AboutPage />} />
    <Route path="digital-human-experience" element={<DigitalHumanExperiencePage />} />
    <Route path="team" element={<TeamPage />} />
    <Route path="contact" element={<ContactPage />} />
    <Route path="join" element={<JoinPage />} />
    <Route path="vr" element={<VRPage />} />
    <Route path="all-in-one" element={<AllInOnePage />} />
    <Route path="privacy" element={<PrivacyPage />} />
    <Route path="terms" element={<TermsPage />} />
    <Route path="sitemap" element={<SitemapPage />} />
    <Route path="mvp-test" element={<MVPTestPage />} />
    <Route path="mvp-china" element={<MVPChinaPage />} />
    <Route path="digital-world" element={<DigitalWorldPage />} />
    <Route path="our-stories" element={<WarmStoriesPage />} />
    <Route path="founder-column" element={<FounderColumnPage />} />
    <Route path="*" element={<NotFoundPage />} />
  </>
);


function App() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!isOnline) {
    return <OfflinePage />;
  }

  return (
    <I18nextProvider i18n={i18n}>
      <Toaster position="top-center" reverseOrder={false} />
      <Router>
        <ScrollToTop />
        <Routes>
          {/* A. 语言前缀匹配 (支持 /en, /ja 等) */}
          <Route path="/:lang" element={<AppLayout />}>
            {BusinessRoutes()}
          </Route>

          {/* B. 根路径匹配 (默认中文) */}
          <Route path="/" element={<AppLayout />}>
            {BusinessRoutes()}
          </Route>
        </Routes>
      </Router>
    </I18nextProvider>
  )
}

export default App
